import { Button, Label, LinkButton, UIHeading4, UIText1 } from '@cian/ui-kit';
import { IconFileTrash16 } from '@cian/ui-kit-design-tokens/icons';
import { useBooleanState } from '@cian/react-utils';
import * as React from 'react';

import { ESavedSearchFrequency, ISavedSearch } from '../../../../types/savedSearch';
import { Status } from '../Status';
import { RemoveModal } from '../RemoveModal';
import { trackChangeScheduleSavedSearch, trackClickDeleteSavedSearch, trackNavigateToSavedSearch } from './tracking';

import styles from './styles.css';

export interface ISearchCardsItemProps extends ISavedSearch {
  description?: string | null;
  title?: string;
  frequency?: string | null;
  selectedFrequency?: string | null;
  link?: string;
  newOffersCount?: number | null;
  status?: string;
  hasNewOffers: boolean;
  isEmailConfirmed: boolean;
  onFrequencyChange: (frequency: ESavedSearchFrequency | null) => void;
  onRemove(): void;
}

export const SearchCardsItem: React.FC<ISearchCardsItemProps> = ({
  title,
  description,
  frequency,
  selectedFrequency,
  isEmailConfirmed,
  link,
  newOffersCount,
  status,
  hasNewOffers,
  onFrequencyChange,
  onRemove,
}) => {
  const {
    state: isSubscriptionModalOpened,
    setTrue: openSubscriptionModal,
    setFalse: closeSubscriptionModal,
  } = useBooleanState();

  const handleChange = (value: ESavedSearchFrequency) => {
    let freq: ESavedSearchFrequency | null = null;
    if (value !== ESavedSearchFrequency.None) {
      freq = value;
    }

    onFrequencyChange(freq);
    trackChangeScheduleSavedSearch(freq, link);
  };

  const handleRemoveClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      openSubscriptionModal();
      trackClickDeleteSavedSearch(link);
    },
    [openSubscriptionModal],
  );

  const handleCardClick = React.useCallback(() => {
    if (link) {
      window.open(link, '_blank');
      trackNavigateToSavedSearch(link);
    }
  }, [link]);

  const handleNavigateClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    trackNavigateToSavedSearch(link);
  }, []);

  return (
    <>
      <div className={styles['container']} onClick={handleCardClick}>
        <div className={styles['content']} data-name="search-content">
          <div className={styles['title']} data-name="search-title">
            <UIHeading4>{title}</UIHeading4>
          </div>
          {description && (
            <div className={styles['description']} data-name="search-description">
              <UIText1>{description}</UIText1>
            </div>
          )}
          <div className={styles['status']} data-name="search-status">
            <Status
              status={status}
              frequency={frequency}
              selectedFrequency={selectedFrequency}
              isEmailConfirmed={isEmailConfirmed}
              onChange={handleChange}
            />
          </div>
          <div className={styles['buttons']} data-name="search-buttons">
            <LinkButton href={link} target="_blank" theme="fill_secondary" size="M" onClick={handleNavigateClick}>
              Перейти в поиск
            </LinkButton>
            <Button
              size="M"
              theme="fill_error_secondary"
              beforeIcon={<IconFileTrash16 color="icon-negative-default" />}
              onClick={handleRemoveClick}
            >
              Удалить
            </Button>
          </div>
        </div>
        <div data-name="search-label">
          {hasNewOffers && (
            <Label
              background="accent-positive-secondary"
              color="text-positive-default"
              data-name="has-new-offers-label"
            >
              {`${newOffersCount} новых`}
            </Label>
          )}
        </div>
      </div>
      <RemoveModal isOpen={isSubscriptionModalOpened} onClose={closeSubscriptionModal} onRemove={onRemove} />
    </>
  );
};
